import type { TFunction } from 'i18next';
import type { ApprovalAttributes } from '@/types/approval';

/** 생산비 마감(회계) — 레거시 appr_edit_forms_production_cost_settlement */
export const PRODUCTION_COST_TYPE = 216;

export const isProductionCostType = (apvType: number): boolean =>
  apvType === PRODUCTION_COST_TYPE;

export const PRODUCTION_COST_MONTH_MISSING_MESSAGE =
  '생산비 마감 월을 선택해 주세요.';
export const PRODUCTION_COST_NOT_LOADED_MESSAGE =
  '생산비 데이터 조회가 완료된 후 저장할 수 있습니다.';
export const PRODUCTION_COST_LOAD_FAILED_MESSAGE =
  '생산비 데이터를 불러올 수 없습니다.';

export type ProductionCostAreaRow = {
  gtext?: string | null;
  waers?: string | null;
  amount?: number | null;
};

export type ProductionCostGroup = {
  rows?: ProductionCostAreaRow[] | null;
  subtotal?: number | null;
};

export type ProductionCostLoanRow = {
  content?: string | null;
  amounts?: Record<string, number | null> | null;
  rowTotal?: number | null;
};

export type ProductionCostDifferenceItem = {
  code?: string | number | null;
  amount?: number | null;
};

/** SAP 집계 원문 — 저장 data.api_data 에 그대로 보관 */
export type ProductionCostSettlement = {
  groupwareDocumentNumber?: string | null;
  productionCost?: {
    payable?: ProductionCostGroup | null;
    tradePayable?: ProductionCostGroup | null;
    total?: number | null;
  } | null;
  externalFinancing?: {
    issuanceDates?: string[] | null;
    rows?: ProductionCostLoanRow[] | null;
    columnTotals?: Record<string, number | null> | null;
    total?: number | null;
  } | null;
  difference?: {
    diffAmount?: number | null;
    items?: ProductionCostDifferenceItem[] | null;
    total?: number | null;
  } | null;
};

export type ProductionCostDiffNote = { code: string; note: string };

/** 저장 data — 키 이름은 레거시 조회 화면이 읽는 그대로 */
export type ProductionCostData = {
  closing_month: string;
  gwdocno: string;
  api_data: ProductionCostSettlement;
  editor_content?: string;
  diff_notes?: ProductionCostDiffNote[];
};

export type ProductionCostDraftSource = {
  code: number;
  attributes: {
    groupwareDocumentNumber: string | null;
    data: ProductionCostData;
  };
  display?: {
    subject?: string;
    closingPeriod?: { start: string; end: string };
    settlement?: ProductionCostSettlement;
  };
};

export type ProductionCostState = {
  /** YYYY-MM */
  closingMonth: string | null;
  loading: boolean;
  /** 조회 성공 결과. 조회 전·조회 중·실패는 null */
  data: ProductionCostData | null;
  /** 차이내역 행별 내용 — 구분 코드별 */
  notes: Record<string, string>;
  /** 편집 진입 시 저장돼 있던 결재묶음번호 — 재조회 응답에 번호가 없으면 이 값 유지 */
  savedGroupwareDocumentNumber: string;
};

export const createProductionCostState = (
  init: Partial<ProductionCostState> = {},
): ProductionCostState => ({
  closingMonth: null,
  loading: false,
  data: null,
  notes: {},
  savedGroupwareDocumentNumber: '',
  ...init,
});

/** 차이내역 구분 — 레거시 DIFF_LABELS. 데이터 없거나 0원이어도 모두 노출 */
export const PRODUCTION_COST_DIFF_LABELS: Record<string, string> = {
  '10': '소비자클레임대',
  '20': '업체 클레임',
  '30': '누적 반품 물대',
  '40': 'MLB COSMETICS',
  '50': '지급보류',
  '60': '기타사유',
  '70': '당월 반품 물대',
};
export const PRODUCTION_COST_CLAIM_CODE = '10';
/** 브랜드명이라 번역하지 않음 */
export const PRODUCTION_COST_BRAND_CODE = '40';
export const PRODUCTION_COST_DEDUCT_CODES = ['20', '30', '40', '50', '60'];
export const PRODUCTION_COST_CARRY_CODE = '70';
export const PRODUCTION_COST_DIFF_CODES = [
  PRODUCTION_COST_CLAIM_CODE,
  ...PRODUCTION_COST_DEDUCT_CODES,
  PRODUCTION_COST_CARRY_CODE,
];

const pad2 = (n: number) => String(n).padStart(2, '0');

/** YYYYMM·YYYY-MM → YYYY-MM. 형식이 아니면 null */
export const toClosingMonth = (value: unknown): string | null => {
  const match = /^(\d{4})-?(\d{2})$/.exec(String(value ?? '').trim());
  if (!match) return null;
  const month = Number(match[2]);
  return month >= 1 && month <= 12 ? `${match[1]}-${match[2]}` : null;
};

/** 고른 달의 1일 ~ 말일 */
export const formatProductionCostPeriod = (closingMonth: string): string => {
  const [year, month] = closingMonth.split('-').map(Number);
  const lastDay = new Date(year, month, 0).getDate();
  return `${closingMonth}-01 ~ ${closingMonth}-${pad2(lastDay)}`;
};

export const buildProductionCostSubject = (
  closingMonth: string,
  t: TFunction,
): string => {
  const [year, month] = closingMonth.split('-').map(Number);
  return t('{{yy}}년 {{m}}월 생산비 외담대 발행의 건', {
    yy: String(year).slice(2),
    m: month,
  });
};

export const formatProductionCostAmount = (n: unknown): string =>
  (typeof n === 'number' && Number.isFinite(n) ? n : 0).toLocaleString('ko-KR');

/** 사업영역명 없거나 금액 0원인 행은 미노출, 사업영역명 순 */
export const visibleProductionCostRows = (
  group: ProductionCostGroup | null | undefined,
): ProductionCostAreaRow[] =>
  (group?.rows ?? [])
    .filter((row) => row?.gtext && row.amount)
    .sort((a, b) => String(a.gtext).localeCompare(String(b.gtext), 'en'));

/** 만기일 열 — columnTotals 키, 없으면 첫 행 amounts 키 */
export const productionCostMaturityKeys = (
  financing: ProductionCostSettlement['externalFinancing'],
): string[] => {
  let keys = Object.keys(financing?.columnTotals ?? {});
  const rows = financing?.rows ?? [];
  if (!keys.length && rows.length) keys = Object.keys(rows[0]?.amounts ?? {});
  return keys.sort();
};

const WEEKDAY_LOCALES: Record<string, string> = {
  ko: 'ko-KR',
  en: 'en-US',
  zh: 'zh-CN',
};

/** yyyymmdd → "2026.8.14 (금)" — 요일은 화면 언어 */
export const formatProductionCostMaturity = (
  key: string,
  lang: string,
): string => {
  const y = Number(key.substring(0, 4));
  const m = Number(key.substring(4, 6));
  const d = Number(key.substring(6, 8));
  if (!y || !m || !d) return key;
  const weekday = new Intl.DateTimeFormat(
    WEEKDAY_LOCALES[lang.slice(0, 2)] ?? 'ko-KR',
    { weekday: 'short' },
  ).format(new Date(y, m - 1, d));
  return `${y}.${m}.${d} (${weekday})`;
};

export const productionCostDiffAmounts = (
  difference: ProductionCostSettlement['difference'],
): Record<string, number> => {
  const amounts: Record<string, number> = {};
  (difference?.items ?? []).forEach((item) => {
    if (item?.code === undefined || item.code === null) return;
    amounts[String(item.code)] = Math.abs(Number(item.amount) || 0);
  });
  return amounts;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/** 저장 data(레거시 문서 포함). 마감 월이 없으면 null */
export const parseProductionCostData = (
  attributes: Record<string, unknown> | null | undefined,
): ProductionCostData | null => {
  const data = attributes?.data;
  if (!isRecord(data)) return null;
  const closingMonth = toClosingMonth(data.closing_month);
  if (!closingMonth) return null;
  return {
    ...(data as Partial<ProductionCostData>),
    closing_month: closingMonth.replace('-', ''),
    gwdocno: typeof data.gwdocno === 'string' ? data.gwdocno : '',
    api_data: isRecord(data.api_data)
      ? (data.api_data as ProductionCostSettlement)
      : {},
    diff_notes: Array.isArray(data.diff_notes)
      ? (data.diff_notes as ProductionCostDiffNote[])
      : [],
  };
};

export const productionCostNotesByCode = (
  notes: ProductionCostDiffNote[] | null | undefined,
): Record<string, string> => {
  const byCode: Record<string, string> = {};
  (notes ?? []).forEach((item) => {
    if (item?.code === undefined || item.code === null) return;
    byCode[String(item.code)] = item.note == null ? '' : String(item.note);
  });
  return byCode;
};

/** 편집 진입 — 레거시처럼 마감 월·차이내역 내용만 복원하고 표는 다시 조회 */
export const productionCostStateFromAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): ProductionCostState => {
  const saved = parseProductionCostData(attributes);
  const savedNumber =
    (typeof attributes?.groupwareDocumentNumber === 'string' &&
      attributes.groupwareDocumentNumber.trim()) ||
    saved?.gwdocno ||
    '';
  return createProductionCostState({
    closingMonth: saved ? toClosingMonth(saved.closing_month) : null,
    notes: productionCostNotesByCode(saved?.diff_notes),
    savedGroupwareDocumentNumber: savedNumber,
  });
};

/** 조회 응답 → 저장 data 뼈대. 응답에 결재묶음번호가 없으면 기존 저장값 유지(레거시 동일) */
export const productionCostDataFromDraftSource = (
  source: ProductionCostDraftSource,
  closingMonth: string,
  savedGroupwareDocumentNumber: string,
): ProductionCostData => {
  const base = source.attributes?.data;
  const apiData = base?.api_data ?? source.display?.settlement ?? {};
  const number =
    source.attributes?.groupwareDocumentNumber ||
    apiData.groupwareDocumentNumber ||
    savedGroupwareDocumentNumber ||
    '';
  return {
    ...base,
    closing_month: closingMonth.replace('-', ''),
    gwdocno: number,
    api_data: apiData,
  };
};

/** 이탈 경고 판정 — 마감 월·차이내역 내용만. 조회 결과는 편집 진입 때 다시 받아 비교에서 제외 */
export const productionCostKey = (
  state: ProductionCostState | null | undefined,
): string =>
  state
    ? JSON.stringify([
        state.closingMonth,
        PRODUCTION_COST_DIFF_CODES.map((code) => state.notes[code] ?? ''),
      ])
    : '';

export const checkProductionCostBeforeSave = (
  state: ProductionCostState | null | undefined,
): string | null => {
  if (!state?.closingMonth) return PRODUCTION_COST_MONTH_MISSING_MESSAGE;
  if (state.loading || !state.data) return PRODUCTION_COST_NOT_LOADED_MESSAGE;
  return null;
};

/** 저장 attributes — data 에 본문·차이내역 내용을 덧붙임(레거시 save_doc_production_cost_settlement) */
export const buildProductionCostAttributes = (
  state: ProductionCostState | null | undefined,
  content: string,
  editorContent: string,
): ApprovalAttributes | null => {
  if (!state?.data) return null;
  return {
    groupwareDocumentNumber: state.data.gwdocno || null,
    content,
    data: {
      ...state.data,
      editor_content: editorContent,
      diff_notes: PRODUCTION_COST_DIFF_CODES.map((code) => ({
        code,
        note: state.notes[code] ?? '',
      })),
    },
  };
};
